// Close-master verification — one page for all eight legacy "/Home/VerifyClose<Type>" screens
// (Bank Account, IT Code, Cost Center, Account Head, Sub Account Head, Client, Sub Client,
// Term Loan Agency). They share the same closing workflow: a ClosingDetails row per master, the
// role's CloseLevelAccess (VerificationLevel → Verify; ApproveLevel → Approve / Reject, with an
// editable closing date at the final level) and a list of other transactions still pending against
// the master. Only routes, identity fields and payload property names differ — see CLOSE_TYPES.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Archive, Clock, AlertTriangle, CheckCircle, XCircle, ShieldCheck, Send, Landmark, Hash,
    Building, BookMarked, BookOpenCheck, Users, UserSquare, Handshake,
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchCloseQueue,
    fetchCloseDetail,
    submitCloseNotification,
    clearCloseDetail,
    resetVerifyCloseMaster,
    selectVCMQueue, selectVCMQueueLoading, selectVCMQueueError,
    selectVCMDetail, selectVCMDetailKey, selectVCMDetailLoading, selectVCMDetailError,
    selectVCMSubmitLoading,
} from '../../slices/accountsSlice/verifyCloseMasterSlice';

// Per-type wiring. `key` identifies a queue row; `fields` are the identity fields shown for it;
// `pendingLists` are [detail property, heading] tables; `payload` builds the save body with the
// property names that type's data layer actually reads.
const CLOSE_TYPES = {
    Bank: {
        label: 'Bank Account',
        icon: Landmark,
        queueRoute: 'GetCloseBankDetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'Bank' }),
        key: (r) => r.Bankid,
        title: (r) => r.BankName,
        subtitle: (r) => r.Accountno,
        fields: (r) => [['Bank Id', r.Bankid], ['Bank Name', r.BankName], ['Account Number', r.Accountno], ['Nature of Group', r.NatureGroupName]],
        detailRoute: 'GetBankCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ BankId: r.Bankid, Roleid: roleId }),
        pendingLists: [['BankPendingList', 'Verifications pending for this bank']],
        saveRoute: 'SaveBankCloseNotifications',
        payload: (r, f) => ({ Action: f.access, Bankid: r.Bankid, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
    IT: {
        label: 'IT Code',
        icon: Hash,
        queueRoute: 'GetCloseITDetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'IT' }),
        key: (r) => r.Itid,
        title: (r) => r.ItName,
        subtitle: (r) => r.ItCode,
        fields: (r) => [['IT Code', r.ItCode], ['IT Name', r.ItName], ['Nature of Group', r.NatureGroupName]],
        detailRoute: 'GetITCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ itId: r.Itid, Roleid: roleId, ITCode: r.ItCode }),
        pendingLists: [['lstITPending', 'Verifications pending for this IT code']],
        openHeadsList: ['lstITDcaSubdca', 'Open account heads under this IT code'],
        saveRoute: 'CloseITNotifications',
        payload: (r, f) => ({ Action: f.access, Itid: r.Itid, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, CreatedBy: f.user, RoleId: f.roleId }),
    },
    CostCenter: {
        label: 'Cost Center',
        icon: Building,
        queueRoute: 'GetCloseCCDetailsbyRoleid',
        queueParams: ({ roleId, userId }) => ({ Roleid: roleId, Type: 'CostCenter', UID: userId }),
        key: (r) => r.CCCode,
        title: (r) => r.CCName,
        subtitle: (r) => r.CCCode,
        fields: (r) => [['CC Code', r.CCCode], ['CC Name', r.CCName], ['CC Type', r.CCType]],
        detailRoute: 'GetCCCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ CCCode: r.CCCode, Roleid: roleId, CCType: r.CCType }),
        pendingLists: [['lstCCPending', 'Verifications pending for this cost center']],
        saveRoute: 'SaveCCCloseNotifications',
        payload: (r, f) => ({ Action: f.access, CCCode: r.CCCode, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, UID: f.userId, Createdby: f.user, RoleId: f.roleId }),
    },
    DCA: {
        label: 'Account Head',
        icon: BookMarked,
        queueRoute: 'GetCloseDCADetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'DCA' }),
        key: (r) => r.DCAID,
        title: (r) => r.DCAName,
        subtitle: (r) => r.DCACode,
        fields: (r) => [['Account Head Code', r.DCACode], ['Account Head Name', r.DCAName], ['Type', r.DCATypeName]],
        detailRoute: 'GetDCACloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ Dcaid: r.DCAID, DCACode: r.DCACode, Roleid: roleId }),
        pendingLists: [['lstDCAPending', 'Verifications pending for this account head']],
        saveRoute: 'CloseDcaNotifications',
        // spCloseDcaNotifications is fed from Remarks / CloseDate / Status on this entity.
        payload: (r, f) => ({ Action: f.access, DCACode: r.DCACode, Remarks: f.remarks, CloseDate: f.closingDate, Status: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
    SubDCA: {
        label: 'Sub Account Head',
        icon: BookOpenCheck,
        queueRoute: 'GetCloseSubDCADetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'SubDCA' }),
        key: (r) => r.SubDCACodeID,
        title: (r) => r.SubDCAName,
        subtitle: (r) => r.SubDCACode,
        fields: (r) => [['Account Head', r.DCAName], ['Sub Account Head Code', r.SubDCACode], ['Sub Account Head Name', r.SubDCAName], ['IT Code', r.ITCodeDescription]],
        detailRoute: 'GetSubDCACloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ SDCACode: r.SubDCACode, Roleid: roleId }),
        pendingLists: [['lstsubDCAPending', 'Verifications pending for this sub account head']],
        saveRoute: 'CloseSubDcaNotifications',
        payload: (r, f) => ({ Action: f.access, SubDCACode: r.SubDCACode, CloseRemarks: f.remarks, ClosingDate: f.closingDate, Status: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
    Client: {
        label: 'Client',
        icon: Users,
        queueRoute: 'GetCloseClientDetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'Client' }),
        key: (r) => r.Client_Code,
        title: (r) => r.Client_Name,
        subtitle: (r) => r.Client_Code,
        fields: (r) => [['Client Code', r.Client_Code], ['Client Name', r.Client_Name], ['Nature of Group', r.NatureGroupName]],
        detailRoute: 'GetClientCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ ClientCode: r.Client_Code, Roleid: roleId }),
        pendingLists: [['lstClientPending', 'Verifications pending for this client'], ['lstSubclientpending', 'Verifications pending for its sub clients']],
        saveRoute: 'CloseClientNotifications',
        payload: (r, f) => ({ CloseAction: f.access, Client_Code: r.Client_Code, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
    SubClient: {
        label: 'Sub Client',
        icon: UserSquare,
        queueRoute: 'GetCloseSubClientDetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'SubClient' }),
        key: (r) => r.SubClientCode,
        title: (r) => r.Branch || r.SubClientCode,
        subtitle: (r) => r.SubClientCode,
        fields: (r) => [['Client Code', r.ClientCode], ['Sub Client Code', r.SubClientCode], ['Branch', r.Branch], ['Nature of Group', r.NatureGroupName]],
        detailRoute: 'GetSubClientCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ SubClientCode: r.SubClientCode, Roleid: roleId }),
        pendingLists: [['lstSubclientpending', 'Verifications pending for this sub client']],
        saveRoute: 'CloseSubClientNotifications',
        payload: (r, f) => ({ CloseAction: f.access, SubClientCode: r.SubClientCode, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
    TermLoanAgency: {
        label: 'Term Loan Agency',
        icon: Handshake,
        queueRoute: 'GetCloseTLAgemcyDetailsbyRoleid',
        queueParams: ({ roleId }) => ({ Roleid: roleId, Type: 'TermLoanAgency' }),
        key: (r) => r.ID,
        title: (r) => r.AgencyName,
        subtitle: (r) => r.AgencyId,
        fields: (r) => [['Agency Code', r.AgencyId], ['Agency Name', r.AgencyName], ['Nature of Group', r.NatureGroupName]],
        detailRoute: 'GetAgencyCloseVerificationPendings',
        detailParams: (r, { roleId }) => ({ AgencyId: r.ID, Roleid: roleId, AgencyCode: r.AgencyId }),
        pendingLists: [['lstAgencyPending', 'Verifications pending for this agency']],
        saveRoute: 'CloseAgencyNotifications',
        payload: (r, f) => ({ Action: f.access, AgencyId: r.AgencyId, CloseRemarks: f.remarks, ClosingDate: f.closingDate, CloseStatus: f.status, Createdby: f.user, RoleId: f.roleId }),
    },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TERMINAL = ['Approved', 'Rejected'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const todayIso = () => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
// Closing date comes back as "25-Sep-2026" (Bank: string) or an ISO DateTime (other entities).
const toIsoDate = (v) => {
    if (!v) return '';
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v).trim());
    if (m) {
        const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
        return month ? `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
    }
    const iso = String(v).slice(0, 10);
    return iso.startsWith('0001') ? '' : iso;
};
// "2026-09-25" → "25-Sep-2026" (legacy's datepicker format — unambiguous for every entity's binder).
const isoToLegacy = (v) => {
    const [y, mo, d] = String(v || '').split('-');
    return y && mo && d ? `${d}-${MONTHS[Number(mo) - 1]}-${y}` : '';
};

// Legacy CloseX partial rules: who may act on a closing request that is in progress.
const resolveCloseAction = (detail) => {
    if (!detail) return { canAct: false, reason: '' };
    const access = detail.CloseLevelAccess;
    const status = String(detail.CloseStatus ?? '');
    const current = Number(detail.CurrentRoleLevel);
    const min = Number(detail.CloseMinRoleLevel);
    const max = Number(detail.CloseMaxRoleLevel);
    const inProgress = Number(detail.CloseNotificationExist) === 1 && status !== 'Rejected';

    if (!inProgress) return { canAct: false, reason: 'The closing request has not been initiated (or was rejected).' };
    if (access !== 'ApproveLevel' && access !== 'VerificationLevel') {
        return { canAct: false, reason: 'Your role is not a verification / approval level for this closing.' };
    }
    if (TERMINAL.includes(status)) return { canAct: false, reason: `This closing request is already ${status.toLowerCase()}.` };

    const numStatus = Number(status);
    if (access === 'VerificationLevel') {
        if (numStatus === current) return { canAct: false, reason: 'You have already verified this closing request.' };
        return { canAct: true, options: ['Verify'], dateEditable: false };
    }
    // ApproveLevel — the final approver may change the closing date.
    const finalLevel = max === current;
    const firstApproval = finalLevel && numStatus === min && min + 1 === current;
    const laterApproval = finalLevel && numStatus > min && numStatus < max;
    if (numStatus === min && min + 1 !== current) return { canAct: false, reason: 'Waiting for the verification level before approval.' };
    return { canAct: true, options: ['Approve', 'Reject'], dateEditable: firstApproval || laterApproval };
};

const StatusBadge = () => (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Closing Pending</span>
);

const PendingTable = ({ title, rows }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
        {rows.length === 0 ? (
            <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Nothing pending</p>
        ) : (
            <div className="overflow-x-auto rounded-xl border border-amber-200 dark:border-amber-700">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-amber-50 dark:bg-amber-900/20">
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase">Operation</th>
                            <th className="px-4 py-2 text-right text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase">Pending</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p, i) => (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{p.PaymentTypeName || `Workflow ${p.WorkFlowLevelId}`}</td>
                                <td className="px-4 py-2 text-right font-bold text-amber-700 dark:text-amber-400">{p.NoofPendings}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

const VerifyCloseMaster = ({ closeType, notificationData, onNavigate }) => {
    const config = CLOSE_TYPES[closeType] || CLOSE_TYPES.Bank;
    const Icon = config.icon || Archive;

    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const userId = userData?.userId || userData?.UID || '';

    const queue = useSelector(selectVCMQueue);
    const queueLoading = useSelector(selectVCMQueueLoading);
    const queueError = useSelector(selectVCMQueueError);
    const detail = useSelector(selectVCMDetail);
    const detailKey = useSelector(selectVCMDetailKey);
    const detailLoading = useSelector(selectVCMDetailLoading);
    const detailError = useSelector(selectVCMDetailError);
    const submitLoading = useSelector(selectVCMSubmitLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};
    const pageTitle = `Close ${config.label} Verification`;

    const [selectedItem, setSelectedItem] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [status, setStatus] = useState('');
    const [closingDate, setClosingDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';
    const ctx = { roleId, userId };

    const selectedKey = selectedItem ? config.key(selectedItem) : null;
    const closeDetail = selectedItem && detailKey === selectedKey ? detail : null;
    const decision = resolveCloseAction(closeDetail);

    const loadQueue = () => {
        if (roleId) dispatch(fetchCloseQueue({ route: config.queueRoute, params: config.queueParams(ctx) }));
    };

    useEffect(() => {
        dispatch(resetVerifyCloseMaster());
        setSelectedItem(null);
        loadQueue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId, closeType]);

    useEffect(() => () => { dispatch(resetVerifyCloseMaster()); }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearCloseDetail());
        dispatch(fetchCloseDetail({ route: config.detailRoute, params: config.detailParams(selectedItem, ctx), key: selectedKey }));
        setStatus('');
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Legacy pre-fills the remarks box and closing date from the ClosingDetails row.
    useEffect(() => {
        if (!closeDetail) return;
        setRemarks(closeDetail.CloseRemarks || '');
        setClosingDate(toIsoDate(closeDetail.ClosingDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailKey, closeDetail?.ClosingDate]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            loadQueue();
            setSelectedItem(null);
            setRemarks('');
            setStatus('');
            setIsLeftPanelCollapsed(false);
            dispatch(clearCloseDetail());
        }, 800);
    };

    const handleSubmit = async () => {
        if (!closeDetail || !decision.canAct) return;
        const errors = [];
        if (!status) errors.push('Select Status');
        if ((status === 'Approve' || status === 'Reject') && !closingDate) errors.push('Select Closing As On Date');
        if (!remarks.trim()) errors.push('Enter Remarks');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = config.payload(selectedItem, {
            access: closeDetail.CloseLevelAccess,
            remarks: remarks.trim(),
            closingDate: isoToLegacy(closingDate),
            status,
            user: getCurrentUser(),
            roleId,
            userId,
        });

        try {
            const result = await dispatch(submitCloseNotification({ route: config.saveRoute, payload })).unwrap();
            const saveStatus = typeof result === 'string' ? result : result?.Data;
            if (saveStatus === 'Notified') {
                const verb = status === 'Verify' ? 'Verified' : status === 'Reject' ? 'Rejected' : 'Approved';
                toast.success(`Closing ${config.label} ${verb} Successfully`);
                finishAndRefresh();
                return;
            }
            toast.error(saveStatus || 'Error Occurred While Sending Notification.');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Sending Notification.');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return config.fields(item).some(([, v]) => String(v ?? '').toLowerCase().includes(q));
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{config.title(item)}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{config.subtitle(item)}</p>
                </div>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] truncate">{item.NatureGroupName || item.CCType || item.DCATypeName || config.label}</span>
                <StatusBadge />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">{config.title(item)}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{config.subtitle(item)}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[260px]">{item.NatureGroupName || item.CCType || item.DCATypeName}</span>
            <span className="ml-auto"><StatusBadge /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDecisionForm = () => {
        if (!decision.canAct) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ {decision.reason || 'No actions available for this closing request'}</p>
                </div>
            );
        }
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Status <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                            {decision.options.map((opt) => {
                                const active = status === opt;
                                const tone = opt === 'Reject'
                                    ? (active ? 'bg-red-600 border-red-600 text-white' : 'border-red-300 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20')
                                    : (active ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20');
                                const OptIcon = opt === 'Reject' ? XCircle : opt === 'Approve' ? CheckCircle : ShieldCheck;
                                return (
                                    <button key={opt} type="button" onClick={() => setStatus(opt)}
                                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 text-sm font-semibold transition-colors ${tone}`}>
                                        <OptIcon className="w-4 h-4" /> {opt}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Closing As On Date {(status === 'Approve' || status === 'Reject') && <span className="text-red-500">*</span>}</label>
                        {decision.dateEditable ? (
                            <input type="date" className={inputCls} value={closingDate} min={todayIso()} onChange={(e) => setClosingDate(e.target.value)} />
                        ) : (
                            <p className="px-3 py-2 text-sm font-semibold text-gray-800 dark:text-gray-100 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                                {closingDate ? isoToLegacy(closingDate) : '—'}
                            </p>
                        )}
                    </div>
                </div>

                <SimpleCommentInput
                    comment={remarks}
                    onCommentChange={(e) => setRemarks(e.target.value)}
                    label="Remarks"
                    placeholder="Enter remarks…"
                    required
                    rows={3}
                    showCharCount
                />

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitLoading || !status || !remarks.trim()}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                        {submitLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                        Submit
                    </button>
                </div>
            </div>
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!closeDetail && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading closing details…</span>
                </div>
            );
        }
        if (detailError || !closeDetail) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Closing details could not be loaded.'}</p>
                </div>
            );
        }

        const pendingCount = config.pendingLists.reduce((sum, [prop]) => sum + (closeDetail[prop] || []).reduce((n, p) => n + (Number(p.NoofPendings) || 0), 0), 0);
        const openHeads = config.openHeadsList ? closeDetail[config.openHeadsList[0]] || [] : [];

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{config.title(selectedItem)}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{config.subtitle(selectedItem)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">Close {config.label}</span>
                        {closeDetail.CloseLevelAccess && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Your access: {closeDetail.CloseLevelAccess.replace(/([a-z])([A-Z])/g, '$1 $2')}</span>
                        )}
                        {pendingCount > 0 && (
                            <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> {pendingCount} pending transaction{pendingCount === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {config.fields(selectedItem).map(([label, value]) => <Field key={label} label={label} value={value} />)}
                        <Field label="Closing As On" value={closeDetail.ClosingDate ? isoToLegacy(toIsoDate(closeDetail.ClosingDate)) : null} />
                    </div>
                </div>

                {config.pendingLists.map(([prop, heading]) => (
                    <PendingTable key={prop} title={heading} rows={closeDetail[prop] || []} />
                ))}

                {config.openHeadsList && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{config.openHeadsList[1]}</p>
                        {openHeads.length === 0 ? (
                            <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> None open</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {openHeads.map((h, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-xs text-amber-800 dark:text-amber-300">
                                        {h.Code} · {h.Name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {renderDecisionForm()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || pageTitle} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Icon}
                badgeText={pageTitle}
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: `Search ${config.label.toLowerCase()}s…`,
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue.map((r) => ({ ...r, __key: String(config.key(r)) })),
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: loadQueue,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: `No ${config.label.toLowerCase()} closings pending verification!`,
                        itemKey: '__key',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: pageTitle,
                        icon: Icon,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Closing Details',
                        icon: Icon,
                        selectedTitle: pageTitle,
                        emptyTitle: `No ${config.label} Selected`,
                        emptyMessage: `Select a ${config.label.toLowerCase()} from the list to review its closing request.`,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyCloseMaster;
